import { zodResolver } from "@hookform/resolvers/zod"
import type { AxiosError } from "axios"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { LeaveConfirmationModal } from "@/components/modals/LeaveConfirmationModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { MediaFilesCreate } from "@/features/communities/components/CommunityForm"
import { CommunityForm } from "@/features/communities/components/CommunityForm"
import { CommunitySuccessModal } from "@/features/communities/components/CommunitySuccessModal"
import { useCreateCommunity } from "@/features/communities/hooks/useCommunityMutations"
import { useCommunityRoles } from "@/features/communities/hooks/useCommunityRoles"
import {
  type CreateCommunityFormData,
  createCommunitySchema,
} from "@/features/communities/schemas/community.schema"
import type { AccessRole } from "@/features/communities/services/community.service"
import type { CommunityUserWithRole, LocationData } from "@/features/communities/types"
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard"
import { useTranslation } from "@/language"

export function CreateCommunityScreen() {
  const { t } = useTranslation("communities")
  const currentUser = useAuthStore(state => state.user)
  const navigate = useNavigate()

  const form = useForm<CreateCommunityFormData>({
    resolver: zodResolver(createCommunitySchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      handle: "",
      type: "public",
      isOrganization: false,
      address: "",
      description: "",
      websiteUrl: "",
      twitterUrl: "",
      instagramUrl: "",
      facebookUrl: "",
      guidelines: "",
      tags: [],
      privacySettings: {
        addToRituals: ["Owners", "Moderators", "Ritualists", "Members"],
        comment: ["Owners", "Moderators", "Ritualists", "Members"],
      },
      selectedUsers: [],
    },
  })

  const { setValue, trigger, watch } = form

  // Warn before leaving with unsaved edits or an in-flight video upload.
  const [isVideoUploading, setIsVideoUploading] = useState(false)
  const unsavedGuard = useUnsavedChangesGuard(form.formState.isDirty || isVideoUploading)

  const { data: communityRoles = [] } = useCommunityRoles()
  const ownerRole = communityRoles.find(r => r.title === "OWNER")
  const ownerRoleId = ownerRole?.id

  const [tags, setTags] = useState<string[]>([])
  const [communityType, setCommunityType] = useState<"public" | "private">("public")
  const [privacySettings, setPrivacySettings] = useState({
    addToRituals: ["Owners", "Moderators", "Ritualists", "Members"],
    comment: ["Owners", "Moderators", "Ritualists", "Members"],
  })
  const [selectedUsers, setSelectedUsers] = useState<CommunityUserWithRole[]>([])
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null)
  const [mediaFiles, setMediaFiles] = useState<MediaFilesCreate>({ images: [], videos: [] })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [createdCommunityId, setCreatedCommunityId] = useState<string | null>(null)

  const createCommunityMutation = useCreateCommunity()
  // CommunityForm registers its native-video flushPending here so we can upload
  // videos queued before the community existed, once we have the new id.
  const nativeVideoFlushRef = useRef<((newCommunityId: string) => Promise<void>) | null>(null)

  useEffect(() => {
    if (currentUser && ownerRoleId && communityRoles.length > 0) {
      if (selectedUsers.length === 0) {
        const ownerUser: CommunityUserWithRole = {
          userId: currentUser.id,
          name: currentUser.name || null,
          email: currentUser.email,
          username: currentUser.username || null,
          profilePictureUrl:
            currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)?.filePath ||
            currentUser.profilePictureUrl ||
            null,
          roleIds: [ownerRoleId],
          roleId: ownerRoleId,
          communityBuilder: true,
          isCurrentUser: true,
        }
        setSelectedUsers([ownerUser])
        setValue("selectedUsers", [ownerUser])
      }
    }
  }, [currentUser, ownerRoleId, communityRoles.length, selectedUsers.length, setValue])

  const handleAddTag = (tag: string) => {
    const newTags = [...tags, tag]
    setTags(newTags)
    setValue("tags", newTags)
    trigger("tags")
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue("tags", updatedTags)
    trigger("tags")
  }

  const handleAddUsers = (users: CommunityUserWithRole[]) => {
    const newUsers = users.filter(u => u.userId !== currentUser?.id)
    const updated = [...selectedUsers, ...newUsers]
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  const handleRemoveUser = (userId: string) => {
    if (userId === currentUser?.id) return
    const updated = selectedUsers.filter(u => u.userId !== userId)
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  const handleUpdateUserRoles = (userId: string, newRoleIds: string[]) => {
    if (userId === currentUser?.id) return
    const updated = selectedUsers.map(u =>
      u.userId === userId ? { ...u, roleIds: newRoleIds } : u,
    )
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
  }

  const handleAddToRitualsChange = useCallback((selected: string[]) => {
    setPrivacySettings(prev => ({ ...prev, addToRituals: selected }))
  }, [])

  const handleCommentChange = useCallback((selected: string[]) => {
    setPrivacySettings(prev => ({ ...prev, comment: selected }))
  }, [])

  const handleCreateCommunity = async () => {
    // Re-entrancy guard: a double-click must not create the community twice and
    // flush the queued videos twice.
    if (isSubmitting) return
    const isFormValid = await trigger()
    if (!isFormValid) {
      setError(t("createCommunity.validationError"))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const privacyMap: Record<string, string> = {
        Everyone: "EVERYONE",
        Followers: "FOLLOWERS",
        "Community Members": "COMMUNITY_MEMBERS",
        Owners: "OWNERS",
        Moderators: "MODERATORS",
        Ritualists: "RITUALISTS",
        Members: "MEMBERS",
      }

      const transformArray = (arr: string[]): AccessRole[] => {
        return arr.map(role => (privacyMap[role] || "EVERYONE") as AccessRole)
      }

      const users = selectedUsers.map(u => {
        const roleIdsArray =
          u.roleIds && u.roleIds.length > 0 ? u.roleIds : u.roleId ? [u.roleId] : []
        return {
          user_id: u.userId,
          role_ids: roleIdsArray,
        }
      })

      const formValues = watch()
      const order = [...mediaFiles.images.map(i => i.id), ...mediaFiles.videos.map(v => v.id)]
      const imageMap = new Map(mediaFiles.images.map(img => [img.id, img]))
      const videoMap = new Map(mediaFiles.videos.map(vid => [vid.id, vid]))

      const allFiles: File[] = []
      const videoTitles: Record<string, string> = {}
      const thumbnailFiles: File[] = []
      const thumbnailIndices: number[] = []
      let coverIndex: number | undefined

      for (const id of order) {
        const img = imageMap.get(id)
        if (img?.file) {
          if (img.isCover) coverIndex = allFiles.length
          allFiles.push(img.file)
          continue
        }
        const vid = videoMap.get(id)
        if (vid?.file) {
          const fileIndex = allFiles.length
          allFiles.push(vid.file)
          if (vid.title) videoTitles[String(fileIndex)] = vid.title
          if (vid.posterFile) {
            thumbnailIndices.push(fileIndex)
            thumbnailFiles.push(vid.posterFile)
          }
        }
      }

      const request = {
        name: formValues.name,
        community_handle: formValues.handle,
        type: communityType.toUpperCase() as "PUBLIC" | "PRIVATE",
        is_organization: formValues.isOrganization,
        street_address: formValues.address || undefined,
        city: selectedLocation?.city,
        state: selectedLocation?.state,
        country: selectedLocation?.country,
        zip_code: selectedLocation?.zipCode,
        lat: selectedLocation?.lat,
        long: selectedLocation?.lng,
        description: formValues.description,
        tags: tags,
        website_url: formValues.websiteUrl || undefined,
        twitter_url: formValues.twitterUrl || undefined,
        instagram_url: formValues.instagramUrl || undefined,
        facebook_url: formValues.facebookUrl || undefined,
        guidelines: formValues.guidelines,
        who_can_add_community_to_rituals: transformArray(privacySettings.addToRituals),
        who_can_comment: transformArray(privacySettings.comment),
        users: users.length > 0 ? users : undefined,
        files: allFiles.length > 0 ? allFiles : undefined,
        coverImageIndex: coverIndex,
        video_titles: Object.keys(videoTitles).length > 0 ? videoTitles : undefined,
        thumbnail_files: thumbnailFiles.length > 0 ? thumbnailFiles : undefined,
        thumbnail_indices: thumbnailIndices.length > 0 ? thumbnailIndices : undefined,
      }

      const result = await createCommunityMutation.mutateAsync(request)

      toast.success(t("createCommunity.successMessage"))

      if (result?.id) {
        // Upload any videos queued during create (no communityId existed yet)
        // to the freshly created community. Best-effort: a failed video doesn't
        // undo the created community.
        try {
          await nativeVideoFlushRef.current?.(result.id)
        } catch (videoErr) {
          toast.error(
            videoErr instanceof Error
              ? `Community saved, but a video failed to upload: ${videoErr.message}`
              : "Community saved, but a video failed to upload.",
          )
        }
        setCreatedCommunityId(result.id)
        // Community is created -- let the success-modal navigation bypass the guard.
        unsavedGuard.allowNavigation()
        setIsSuccessModalOpen(true)
      }
    } catch (err: unknown) {
      const error = err as AxiosError<{ message: string }>
      const errorMessage = error.response?.data?.message || t("createCommunity.errorMessage")
      toast.error(errorMessage)
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <CommunityForm
      mode="create"
      form={form}
      registerNativeVideoFlush={flush => {
        nativeVideoFlushRef.current = flush
      }}
      tags={tags}
      onAddTag={handleAddTag}
      onRemoveTag={handleRemoveTag}
      selectedUsers={selectedUsers}
      onAddUsers={handleAddUsers}
      onRemoveUser={handleRemoveUser}
      onUpdateUserRoles={handleUpdateUserRoles}
      communityType={communityType}
      onCommunityTypeChange={setCommunityType}
      privacySettings={privacySettings}
      onAddToRitualsChange={handleAddToRitualsChange}
      onCommentChange={handleCommentChange}
      selectedLocation={selectedLocation}
      onLocationSelect={setSelectedLocation}
      communityRoles={communityRoles}
      ownerRoleId={ownerRoleId}
      isSubmitting={isSubmitting}
      submitError={error}
      onSubmit={handleCreateCommunity}
      createMediaFiles={mediaFiles}
      onMediaFilesChange={setMediaFiles}
      onUploadingChange={setIsVideoUploading}
      renderModals={() => (
        <>
          <LeaveConfirmationModal
            isOpen={unsavedGuard.isBlocked}
            onKeepEditing={unsavedGuard.cancelLeave}
            onLeave={unsavedGuard.confirmLeave}
          />
          <CommunitySuccessModal
            overlayClass="bg-[rgb(0,0,0)]"
            isOpen={isSuccessModalOpen}
            communityId={createdCommunityId || ""}
            onViewCommunity={() => {
              if (createdCommunityId) {
                navigate(`/communities/${createdCommunityId}`)
              }
            }}
          />
        </>
      )}
    />
  )
}
